/* eslint-disable prefer-arrow-callback */
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Stack,
  Box,
  Paper,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Avatar,
  Chip,
  InputBase,
  Tooltip,
  Dialog,
  DialogContent,
  Zoom,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Globe,
  Plus,
  HelpCircle,
  X,
  CheckCircle2,
  Check,
  ChevronDown,
  Search,
  SlidersHorizontal,
  Monitor,
  Smartphone,
  Apple,
  Rocket,
} from 'lucide-react';

import Page from '../components/Page';

// ─── Animations & Transitions ────────────────────────────────────────────────

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Zoom ref={ref} {...props} />;
});

const floatAnimation = {
  '@keyframes float': {
    '0%': { transform: 'translateY(0px)' },
    '50%': { transform: 'translateY(-12px)' },
    '100%': { transform: 'translateY(0px)' },
  },
  animation: 'float 3s ease-in-out infinite',
};

// ─── Styled Components ───────────────────────────────────────────────────────

const TypeCard = styled(Paper)(({ theme, selected }) => ({
  padding: theme.spacing(2.5, 2),
  borderRadius: 12,
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  position: 'relative',
  transition: 'all 0.2s ease',
  border: selected ? `2px solid #3b5ee6` : '1px solid #eaeaea',
  backgroundColor: selected ? '#f8f9ff' : '#fff',
  boxShadow: selected ? '0 4px 12px rgba(59, 94, 230, 0.08)' : 'none',
  width: '100%',
  minHeight: 130,
  justifyContent: 'flex-start',
  '&:hover': {
    borderColor: selected ? '#3b5ee6' : '#d0d0d0',
  },
}));

const ActiveCheck = styled(Box)({
  position: 'absolute',
  top: 10,
  right: 10,
  width: 20,
  height: 20,
  backgroundColor: '#3b5ee6',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
});

const CustomTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: 8,
    backgroundColor: '#fff',
    transition: 'all 0.2s ease',
    '& fieldset': {
      borderColor: '#e0e0e0',
    },
    '&:hover fieldset': {
      borderColor: '#b0b0b0',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#3b5ee6',
      borderWidth: '2px',
    },
  },
});

const PrimaryButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  padding: '12px 32px',
  textTransform: 'none',
  fontWeight: 600,
  backgroundColor: '#3b5ee6',
  boxShadow: '0 4px 12px rgba(59, 94, 230, 0.2)',
  fontSize: '0.95rem',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#2a48c6',
    boxShadow: '0 6px 16px rgba(59, 94, 230, 0.3)',
  },
  '&.Mui-disabled': {
    backgroundColor: '#b5bce4',
    color: '#fff',
    boxShadow: 'none',
  },
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  border: '1px solid #e0e0e0',
  borderRadius: 8,
  padding: '4px 12px',
  backgroundColor: '#fff',
  height: 44,
  width: '100%',
  transition: 'all 0.2s ease',
  '&:focus-within': {
    borderColor: '#3b5ee6',
    boxShadow: '0 0 0 3px rgba(59, 94, 230, 0.1)',
  },
  [theme.breakpoints.up('sm')]: {
    width: 280,
  },
}));

const CreateButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  padding: '8px 20px',
  textTransform: 'none',
  fontWeight: 600,
  backgroundColor: '#3b5ee6',
  boxShadow: 'none',
  height: 44,
  '&:hover': {
    backgroundColor: '#2a48c6',
    boxShadow: 'none',
  },
}));

const PlatformButton = styled(Button)(({ theme, selected }) => ({
  borderRadius: 8,
  padding: '10px 20px',
  textTransform: 'none',
  fontWeight: 600,
  border: selected ? '2px solid #3b5ee6' : '1px solid #e0e0e0',
  backgroundColor: selected ? '#f8f9ff' : '#fff',
  color: selected ? '#3b5ee6' : '#666',
  boxShadow: 'none',
  '&:hover': {
    borderColor: selected ? '#3b5ee6' : '#b0b0b0',
    backgroundColor: selected ? '#f8f9ff' : '#fcfcfc',
    boxShadow: 'none',
  },
}));

// ─── Constants ───────────────────────────────────────────────────────────────

const APP_TYPES = [
  {
    id: 'vtu',
    name: 'Billpayment App',
    desc: 'Get your own billpayment app',
    icon: (
      <Box sx={{ display: 'flex' }}>
        <Box sx={{ width: 16, height: 16, borderRadius: '50%', border: '2.5px solid #5ee6d2', ml: -0.5 }} />
        <Box sx={{ width: 16, height: 16, borderRadius: '50%', border: '2.5px solid #a85ee6', ml: -0.5 }} />
      </Box>
    ),
  },
  {
    id: 'web',
    name: 'Web to App',
    desc: 'Convert any website to app',
    icon: <Globe size={28} color="#3b5ee6" strokeWidth={1.5} />,
  },
  {
    id: 'custom',
    name: 'Custom App',
    desc: 'Includes Web to app',
    icon: (
      <Box sx={{ display: 'flex' }}>
        <Box sx={{ width: 16, height: 16, borderRadius: '50%', border: '2.5px solid #5ee6d2', ml: -0.5 }} />
        <Box sx={{ width: 16, height: 16, borderRadius: '50%', border: '2.5px solid #a85ee6', ml: -0.5 }} />
      </Box>
    ),
  },
];

const PLATFORMS = [
  { id: 'web', label: 'Web', icon: <Monitor size={18} /> },
  { id: 'android', label: 'Android', icon: <Smartphone size={18} /> },
  { id: 'ios', label: 'iOS', icon: <Apple size={18} /> },
];

const FEATURES_MAP = {
  vtu: [
    'Pre-integrated bill payment and VTU APIs',
    'Automated wallet funding and management',
    'Support for Web, Android, and iOS platforms',
    'Customizable branding and themes',
    'Built-in transaction tracking and analytics',
    'Secure payment gateway integrations',
  ],
  web: [
    'Convert any website to a native app',
    'Works with any CMS platform',
    'Compatible with all web technologies',
    'Real-time website-app sync',
    'Download native Android & iOS (Apple) apps',
    'Compliant with App Store and Play Store',
  ],
  custom: [
    'Tailor-made UI/UX and feature set',
    'Dedicated database and backend architecture',
    'Advanced third-party API integrations',
    'Scalable cloud infrastructure',
    'Comprehensive testing and quality assurance',
    'Dedicated technical support and maintenance',
  ],
};

// ─── Functional Components ───────────────────────────────────────────────────

const LabelWithInfo = ({ children, tooltipText }) => (
  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.5 }}>
    <Typography variant="body2" sx={{ fontWeight: 600, color: '#4a4a4a', fontSize: '0.85rem' }}>
      {children}
    </Typography>
    {tooltipText ? (
      <Tooltip title={tooltipText} placement="top" arrow>
        <Box display="flex" alignItems="center">
          <HelpCircle size={15} color="#b0b0b0" strokeWidth={2} style={{ cursor: 'pointer' }} />
        </Box>
      </Tooltip>
    ) : (
      <HelpCircle size={15} color="#b0b0b0" strokeWidth={2} style={{ cursor: 'pointer' }} />
    )}
  </Stack>
);

// ─── Main Component ──────────────────────────────────────────────────────────

export default function CreateApp() {
  const navigation = useNavigate();
  const user = useSelector((state) => state.auth?.user) || {
    firstName: 'Partner',
    email: 'partner@example.com',
    avatar: 'https://i.pravatar.cc/150?img=11',
  };

  // Form & Modal State
  const [selectedType, setSelectedType] = useState('vtu');
  const [appName, setAppName] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState(['android']);
  const [showComingSoon, setShowComingSoon] = useState(false);

  const handlePlatformToggle = (platformId) => {
    setSelectedPlatforms((prev) =>
      prev.includes(platformId) ? prev.filter((id) => id !== platformId) : [...prev, platformId]
    );
  };

  const handleCreateApp = () => {
    // Collect payload for future use
    const payload = {
      type: selectedType,
      name: appName,
      ...(selectedType === 'web' && { url: websiteUrl }),
      ...(selectedType === 'vtu' && { platforms: selectedPlatforms }),
    };
    console.log('Submitting:', payload);

    // Show the modern coming soon modal instead of navigating
    setShowComingSoon(true);
  };

  // Dynamic Validation
  const isSubmitDisabled = (() => {
    if (!appName.trim()) return true;
    if (selectedType === 'web' && !websiteUrl.trim()) return true;
    if (selectedType === 'vtu' && selectedPlatforms.length === 0) return true;
    return false;
  })();

  return (
    <Page title="Create an App">
      <Box sx={{ minHeight: '100vh', bgcolor: '#fbfbfd', display: 'flex', flexDirection: 'column' }}>
        <Container maxWidth="xl" sx={{ pt: { xs: 3, md: 4 }, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {/* ─── Header Section ─── */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={{ xs: 3, md: 3 }}
            sx={{ mb: { xs: 4, md: 5 }, width: '100%' }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar src={user.profileImage} alt={user.firstName} sx={{ width: 56, height: 56 }} />
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: '#1a1a1a',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'pointer',
                  }}
                >
                  {user.firstName}
                  <ChevronDown size={20} color="#666" />
                </Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  {user.email}
                </Typography>
              </Box>
            </Stack>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              sx={{ width: { xs: '100%', md: 'auto' } }}
            >
              <SearchContainer>
                <Search size={18} color="#999" sx={{ flexShrink: 0 }} />
                <InputBase placeholder="Search" sx={{ ml: 1, flex: 1, fontSize: '0.9rem' }} />
              </SearchContainer>

              <Stack direction="row" spacing={2} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                <IconButton sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: '9px', flexShrink: 0 }}>
                  <SlidersHorizontal size={20} color="#666" />
                </IconButton>

                <CreateButton
                  variant="contained"
                  startIcon={<Plus size={18} />}
                  onClick={handleCreateApp}
                  disabled={isSubmitDisabled}
                  sx={{ flexGrow: { xs: 1, sm: 0 } }}
                >
                  Create app
                </CreateButton>
              </Stack>
            </Stack>
          </Stack>

          {/* ─── Main Content Container ─── */}
          <Paper
            elevation={0}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              flexGrow: 1,
              mb: 6,
              borderRadius: 3,
              overflow: 'hidden',
              border: '1px solid #eaeaea',
              boxShadow: '0 10px 30px rgba(0,0,0,0.02)',
            }}
          >
            {/* ─── Left Side: Form ─── */}
            <Box sx={{ flex: { xs: 1, md: 0.65 }, p: { xs: 3, sm: 5, lg: 7 }, bgcolor: '#ffffff' }}>
              <Box sx={{ maxWidth: 700 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#181b31', mb: 5, letterSpacing: '-0.5px' }}>
                  Create an app
                </Typography>

                <Stack spacing={4}>
                  <Box>
                    <LabelWithInfo tooltipText="Select the primary architecture and feature set for your application.">
                      App type*
                    </LabelWithInfo>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
                      {APP_TYPES.map((type) => (
                        <TypeCard
                          key={type.id}
                          selected={selectedType === type.id}
                          onClick={() => {
                            setSelectedType(type.id);
                            if (type.id !== 'web') setWebsiteUrl('');
                            if (type.id !== 'vtu') setSelectedPlatforms(['android']);
                          }}
                        >
                          {selectedType === type.id && (
                            <ActiveCheck>
                              <Check size={14} strokeWidth={3} />
                            </ActiveCheck>
                          )}
                          <Box sx={{ mt: 1, mb: 2, height: 32, display: 'flex', alignItems: 'center' }}>
                            {type.icon}
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, color: '#181b31', fontSize: '0.85rem', mb: 0.5 }}
                          >
                            {type.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#888', fontSize: '0.75rem', lineHeight: 1.3 }}>
                            {type.desc}
                          </Typography>
                        </TypeCard>
                      ))}
                    </Box>
                  </Box>

                  <Box>
                    <LabelWithInfo tooltipText="This will be the public name displayed to users on their devices and the App Store.">
                      Give your app a name*
                    </LabelWithInfo>
                    <CustomTextField
                      fullWidth
                      value={appName}
                      onChange={(e) => setAppName(e.target.value)}
                      variant="outlined"
                      size="medium"
                      placeholder="e.g. My Awesome Store"
                    />
                  </Box>

                  {selectedType === 'web' && (
                    <Box>
                      <LabelWithInfo tooltipText="Enter the full URL of the website you want to convert to a native application.">
                        Website*
                      </LabelWithInfo>
                      <CustomTextField
                        fullWidth
                        value={websiteUrl}
                        onChange={(e) => setWebsiteUrl(e.target.value)}
                        size="medium"
                        placeholder="www.example.com"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment
                              position="start"
                              sx={{ pr: 1.5, borderRight: '1px solid #e0e0e0', mr: 1.5 }}
                            >
                              <Typography sx={{ color: '#666', fontSize: '0.9rem', fontWeight: 500 }}>
                                https://
                              </Typography>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                  )}

                  {selectedType === 'vtu' && (
                    <Box>
                      <LabelWithInfo tooltipText="Choose the operating systems and environments where your app will be available.">
                        Select Platforms*
                      </LabelWithInfo>
                      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                        {PLATFORMS.map((platform) => {
                          const isSelected = selectedPlatforms.includes(platform.id);
                          return (
                            <PlatformButton
                              key={platform.id}
                              selected={isSelected}
                              onClick={() => handlePlatformToggle(platform.id)}
                              startIcon={platform.icon}
                            >
                              {platform.label}
                            </PlatformButton>
                          );
                        })}
                      </Stack>
                    </Box>
                  )}

                  <Box sx={{ pt: 2 }}>
                    <PrimaryButton variant="contained" disabled={isSubmitDisabled} onClick={handleCreateApp}>
                      Create app
                    </PrimaryButton>
                  </Box>
                </Stack>
              </Box>
            </Box>

            {/* ─── Right Side: Dynamic Product Features ─── */}
            <Box
              sx={{
                display: { xs: 'none', md: 'block' },
                flex: { md: 0.35 },
                bgcolor: '#f8fafc',
                borderLeft: '1px solid #eaeaea',
                p: { sm: 5, lg: 7 },
                position: 'relative',
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#181b31', letterSpacing: '-0.5px' }}>
                  Product features
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => navigation(-1)}
                  sx={{ color: '#a0a0a0', bgcolor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
                >
                  <X size={18} />
                </IconButton>
              </Stack>

              <Stack spacing={3}>
                {FEATURES_MAP[selectedType]?.map((feature, idx) => (
                  <Stack direction="row" spacing={2} alignItems="flex-start" key={idx}>
                    <CheckCircle2 size={20} color="#3b5ee6" style={{ flexShrink: 0, marginTop: 1 }} strokeWidth={2} />
                    <Typography
                      variant="body2"
                      sx={{ color: '#4a5568', fontWeight: 500, lineHeight: 1.6, fontSize: '0.9rem' }}
                    >
                      {feature}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* ─── Animated "Coming Soon" Modal ─── */}
      <Dialog
        open={showComingSoon}
        onClose={() => setShowComingSoon(false)}
        TransitionComponent={Transition}
        keepMounted
        PaperProps={{
          sx: {
            borderRadius: 4,
            padding: 2,
            maxWidth: 400,
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 24px 48px rgba(0,0,0,0.1)',
          },
        }}
      >
        <DialogContent sx={{ py: 4 }}>
          <Stack alignItems="center" spacing={3}>
            {/* Animated Icon Container */}
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: '#eef2ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 0 0 12px #f8faff',
                mb: 1,
                ...floatAnimation, // Apply the CSS keyframes here
              }}
            >
              <Rocket size={40} color="#3b5ee6" strokeWidth={1.5} />
            </Box>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#181b31', mb: 1.5, letterSpacing: '-0.5px' }}>
                Coming Soon!
              </Typography>
              <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.6, px: 2 }}>
                We're currently polishing the magic behind the scenes. App creation will be available in the next
                update.
              </Typography>
            </Box>

            <Button
              variant="contained"
              onClick={() => setShowComingSoon(false)}
              sx={{
                mt: 2,
                borderRadius: 8,
                py: 1.2,
                px: 4,
                textTransform: 'none',
                fontWeight: 600,
                backgroundColor: '#181b31',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#000',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                },
              }}
            >
              Got it, thanks
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
